// Dots for CSS scroll-snap rows that only scroll on small screens.
function bind(root: HTMLElement): void {
	const track = root.querySelector<HTMLElement>('[data-armada-snap-track]');
	const dots = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-armada-snap-dot]'));

	if (!track || !dots.length) {
		return;
	}

	const items = Array.from(track.children) as HTMLElement[];
	// One dot can stand for several cards, e.g. a pair per screen.
	const step = Math.max(1, Number(root.dataset.armadaSnapStep) || 1);
	let frame = 0;

	const maxScroll = (): number => track.scrollWidth - track.clientWidth;

	// scroll-padding written with max()/% comes back unresolved; the track padding matches it.
	const paddingStart = (): number => {
		const styles = getComputedStyle(track);
		const scrollPadding = parseFloat(styles.scrollPaddingLeft);

		return Number.isFinite(scrollPadding) ? scrollPadding : parseFloat(styles.paddingLeft) || 0;
	};

	// Item's left edge inside the scrolled content, whatever its offsetParent is.
	const position = (item: HTMLElement): number =>
		item.getBoundingClientRect().left - track.getBoundingClientRect().left - track.clientLeft + track.scrollLeft;

	// Where the track scrolls to for an item, following its scroll-snap-align.
	const offset = (item: HTMLElement): number => {
		const left =
			getComputedStyle(item).scrollSnapAlign.includes('start')
				? position(item) - paddingStart()
				: position(item) - (track.clientWidth - item.offsetWidth) / 2;

		return Math.max(0, Math.min(left, maxScroll()));
	};

	let locked = -1;
	let unlock = 0;

	const mark = (active: number): void => {
		dots.forEach((dot, index) => {
			dot.classList.toggle('is-active', index === active);
			dot.setAttribute('aria-current', index === active ? 'true' : 'false');
		});
	};

	const update = (): void => {
		frame = 0;

		// loop.ts moves the same row with transforms and lights the dots itself.
		if (root.classList.contains('is-loop')) {
			return;
		}

		// While a tapped dot's scroll runs, keep that dot lit instead of the ones passed on the way.
		if (locked >= 0) {
			return;
		}

		let active = 0;
		let best = Infinity;

		items.forEach((item, index) => {
			const distance = Math.abs(offset(item) - track.scrollLeft);

			if (distance < best) {
				best = distance;
				active = index;
			}
		});

		// Several items can share the last scroll position; the end of the row is the last one.
		if (track.scrollLeft >= maxScroll() - 2) {
			active = items.length - 1;
		}

		mark(Math.min(dots.length - 1, Math.ceil(active / step)));
	};

	track.addEventListener(
		'scroll',
		() => {
			if (!frame) {
				frame = requestAnimationFrame(update);
			}
		},
		{ passive: true }
	);

	dots.forEach((dot, index) => {
		dot.addEventListener('click', () => {
			const item = items[index * step];

			if (item && !root.classList.contains('is-loop')) {
				locked = index;
				mark(index);
				window.clearTimeout(unlock);
				unlock = window.setTimeout(() => {
					locked = -1;
				}, 700);
				track.scrollTo({ left: offset(item), behavior: 'smooth' });
			}
		});
	});
}

/*
 * A row that scrolls sideways has to be reachable with the keyboard, and a row that fits on the
 * screen must not become a tab stop that goes nowhere — which one it is changes with the width.
 */
function keyboardScroll(): void {
	const tracks = Array.from(document.querySelectorAll<HTMLElement>('[data-armada-snap-track]'));

	if (!tracks.length) {
		return;
	}

	const sync = (): void => {
		tracks.forEach((track) => {
			const overflow = getComputedStyle(track).overflowX;
			const scrolls = (overflow === 'auto' || overflow === 'scroll') && track.scrollWidth > track.clientWidth + 1;

			if (scrolls) {
				track.tabIndex = 0;
			} else {
				track.removeAttribute('tabindex');
			}
		});
	};

	sync();
	window.addEventListener('resize', sync, { passive: true });

	// A picture that arrives late can make a row scroll that did not scroll before.
	window.addEventListener('load', sync, { once: true });
	document.addEventListener('load', (event) => {
		if ((event.target as HTMLElement)?.closest?.('[data-armada-snap-track]')) {
			sync();
		}
	}, true);
}

export default function initSnapDots(): void {
	document.querySelectorAll<HTMLElement>('[data-armada-snap]').forEach(bind);
	keyboardScroll();
}
